<template>
    <div class="import-control-result">
        <div class="import-control-result__context mb-2">
            <div>
                <span>Manifest</span>
                <strong>{{ manifestName || '—' }}</strong>
            </div>
            <div>
                <span>Version</span>
                <strong>{{ versionLabel || '—' }}</strong>
            </div>
            <div>
                <span>Source</span>
                <strong>{{ sourceName || '—' }}</strong>
            </div>
        </div>

        <div class="import-control-result__heading mb-2">
            <div>
                <div class="d-flex align-items-center mb-25">
                    <h4 class="mb-0 mr-75">
                        {{ succeeded ? 'Ready for execution' : 'Control found errors' }}
                    </h4>
                    <b-badge :variant="succeeded ? 'light-success' : 'light-danger'">
                        {{ controlRun.status }}
                    </b-badge>
                </div>
                <div class="text-muted">
                    {{ succeeded
                        ? 'The source is valid and the execution plan is ready.'
                        : 'Review the highlighted rows and cells before running control again.' }}
                </div>
            </div>
            <div class="d-flex align-items-center">
                <b-button variant="outline-secondary" class="mr-75" @click="$emit('back')">
                    <feather-icon icon="ChevronLeftIcon" size="16" class="mr-50" />
                    Back to preparation
                </b-button>
                <b-button
                    v-if="succeeded"
                    variant="primary"
                    :disabled="!canStart || starting"
                    @click="$emit('execute')"
                >
                    <b-spinner v-if="starting" small class="mr-50" />
                    {{ starting ? 'Starting import' : 'Start import' }}
                </b-button>
            </div>
        </div>

        <div v-if="mappingFactCards.length" class="import-control-result__facts mb-2">
            <article v-for="fact in mappingFactCards" :key="fact.key">
                <div class="text-muted font-small-2">{{ fact.label }}</div>
                <strong :class="fact.valueClass">{{ fact.value }}</strong>
            </article>
        </div>

        <div v-if="previewStatistics.length" class="mb-2">
            <h5 class="mb-1">Planned changes</h5>
            <div class="import-control-result__statistics">
                <article v-for="statistic in previewStatistics" :key="statistic.key">
                    <div class="text-muted font-small-2">{{ statistic.label }}</div>
                    <strong>{{ statistic.value }}</strong>
                </article>
            </div>
        </div>

        <b-alert v-if="warnings.length" variant="warning" show class="mb-2">
            <div class="alert-body">
                <div v-for="(warning, index) in warnings" :key="index">
                    {{ warning }}
                </div>
            </div>
        </b-alert>

        <div v-if="globalIssues.length" class="import-control-result__global-issues mb-2">
            <article v-for="(issue, index) in globalIssues" :key="`${issue.code || 'error'}:${index}`">
                <feather-icon icon="AlertCircleIcon" size="18" />
                <div>
                    <div class="font-weight-bold">{{ issue.message || issue }}</div>
                    <small v-if="issueLocation(issue)" class="text-muted">
                        {{ issueLocation(issue) }}
                    </small>
                </div>
            </article>
        </div>

        <section v-if="columns.length" class="import-control-result__preview">
            <div class="import-control-result__preview-heading">
                <div>
                    <h5 class="mb-25">Mapped data</h5>
                    <div class="text-muted font-small-2">
                        Values after extraction and field pipelines. Hover a highlighted cell for details.
                    </div>
                </div>
                <b-button-group size="sm">
                    <b-button
                        v-for="option in rowFilterOptions"
                        :key="option.value"
                        :variant="rowFilter === option.value ? 'primary' : 'outline-secondary'"
                        @click="selectRowFilter(option.value)"
                    >
                        {{ option.label }}
                        <span class="ml-25">{{ option.count }}</span>
                    </b-button>
                </b-button-group>
            </div>

            <div v-if="filteredRows.length" class="import-control-result__table-wrap">
                <table class="table table-hover mb-0 import-control-result__table">
                    <thead>
                        <tr>
                            <th class="import-control-result__source-column">Source row</th>
                            <th
                                v-for="(column, columnIndex) in columns"
                                :key="column.key"
                            >
                                <div class="d-flex align-items-center">
                                    <span>{{ column.title || column.key }}</span>
                                    <button
                                        v-if="column.description"
                                        :id="columnTargetId(columnIndex)"
                                        type="button"
                                        class="import-control-result__info ml-50"
                                        :aria-label="`About ${column.title || column.key}`"
                                    >
                                        <feather-icon icon="InfoIcon" size="14" />
                                    </button>
                                    <b-popover
                                        v-if="column.description"
                                        :target="columnTargetId(columnIndex)"
                                        triggers="hover focus"
                                        placement="top"
                                    >
                                        <div>{{ column.description }}</div>
                                        <small class="text-muted">{{ column.key }}</small>
                                    </b-popover>
                                </div>
                                <small class="text-muted font-weight-normal">
                                    {{ column.valueType || 'mixed' }}
                                </small>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(row, rowIndex) in paginatedRows"
                            :key="row.recordId || `${row.source && row.source.physicalTab}:${row.source && row.source.row}:${rowIndex}`"
                            :class="{'import-control-result__row--invalid': !row.valid}"
                        >
                            <td class="import-control-result__source-column">
                                <div class="d-flex align-items-start">
                                    <span
                                        class="import-control-result__row-status mr-75"
                                        :class="row.valid
                                            ? 'import-control-result__row-status--valid'
                                            : 'import-control-result__row-status--invalid'"
                                    >
                                        <feather-icon
                                            :icon="row.valid ? 'CheckIcon' : 'AlertCircleIcon'"
                                            size="14"
                                        />
                                    </span>
                                    <div>
                                        <strong>{{ sourceRowLabel(row) }}</strong>
                                        <small class="d-block text-muted">{{ sourceTabLabel(row) }}</small>
                                    </div>
                                </div>
                                <template v-if="rowIssues(row).length">
                                    <button
                                        :id="rowTargetId(rowIndex)"
                                        type="button"
                                        class="import-control-result__row-issues mt-50"
                                    >
                                        {{ rowIssues(row).length }} row {{ rowIssues(row).length === 1 ? 'issue' : 'issues' }}
                                    </button>
                                    <b-popover
                                        :target="rowTargetId(rowIndex)"
                                        triggers="hover focus"
                                        placement="right"
                                        custom-class="import-control-result__popover"
                                    >
                                        <div
                                            v-for="(issue, issueIndex) in rowIssues(row)"
                                            :key="`${issue.code || 'error'}:${issueIndex}`"
                                            class="import-control-result__popover-issue"
                                        >
                                            <strong>{{ issue.message || issue }}</strong>
                                            <small v-if="issue.component" class="d-block text-muted">
                                                {{ issue.component }}
                                            </small>
                                        </div>
                                    </b-popover>
                                </template>
                            </td>
                            <td
                                v-for="(column, columnIndex) in columns"
                                :key="column.key"
                                :class="cellClass(cell(row, column.key))"
                            >
                                <div
                                    :id="cellTargetId(rowIndex, columnIndex)"
                                    class="import-control-result__cell"
                                    :tabindex="cellIssues(cell(row, column.key)).length ? 0 : null"
                                >
                                    <div class="d-flex align-items-start justify-content-between">
                                        <span class="import-control-result__cell-value">
                                            {{ displayValue(cell(row, column.key).value) }}
                                        </span>
                                        <feather-icon
                                            v-if="cellIssues(cell(row, column.key)).length"
                                            icon="AlertCircleIcon"
                                            size="16"
                                            class="import-control-result__cell-error ml-50"
                                        />
                                    </div>
                                    <small
                                        v-if="showSourceValue(cell(row, column.key))"
                                        class="d-block text-muted mt-25"
                                    >
                                        Source: {{ displayValue(cell(row, column.key).sourceValue) }}
                                    </small>
                                    <small
                                        v-if="cell(row, column.key).state && cell(row, column.key).state !== 'present'"
                                        class="d-block mt-25"
                                    >
                                        {{ humanizeKey(cell(row, column.key).state) }}
                                    </small>
                                </div>
                                <b-popover
                                    v-if="cellIssues(cell(row, column.key)).length"
                                    :target="cellTargetId(rowIndex, columnIndex)"
                                    triggers="hover focus"
                                    placement="top"
                                    custom-class="import-control-result__popover"
                                >
                                    <div
                                        v-for="(issue, issueIndex) in cellIssues(cell(row, column.key))"
                                        :key="`${issue.code || 'error'}:${issueIndex}`"
                                        class="import-control-result__popover-issue"
                                    >
                                        <strong>{{ issue.message || issue }}</strong>
                                        <small v-if="issue.component" class="d-block text-muted">
                                            {{ issue.component }}
                                        </small>
                                    </div>
                                </b-popover>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-else class="import-control-result__empty">
                <feather-icon icon="CheckCircleIcon" size="28" class="mb-75" />
                <div class="font-weight-bold">No {{ rowFilter }} rows</div>
                <small class="text-muted">Choose another filter to inspect the mapped data.</small>
            </div>

            <div v-if="filteredRows.length > perPage" class="d-flex justify-content-between align-items-center p-1">
                <small class="text-muted">
                    Showing {{ pageStart }}–{{ pageEnd }} of {{ filteredRows.length }} rows
                </small>
                <b-pagination
                    v-model="page"
                    :total-rows="filteredRows.length"
                    :per-page="perPage"
                    size="sm"
                    class="mb-0"
                />
            </div>
        </section>

        <div v-else class="import-control-result__empty import-control-result__empty--bordered">
            <feather-icon icon="AlertTriangleIcon" size="28" class="mb-75" />
            <div class="font-weight-bold">Mapped row preview is unavailable</div>
            <small class="text-muted">
                Review the errors above. The backend could not build a mapped dataset for this source.
            </small>
        </div>
    </div>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BButtonGroup,
    BPagination,
    BPopover,
    BSpinner,
} from 'bootstrap-vue'

const FACTS = [
    {key: 'sourceRows', label: 'Source rows'},
    {key: 'filteredRows', label: 'Filtered out'},
    {key: 'mappedRows', label: 'Mapped rows'},
    {key: 'validRows', label: 'Valid rows', valueClass: 'text-success'},
    {key: 'invalidRows', label: 'Invalid rows', valueClass: 'text-danger'},
]

export default {
    name: 'ImportControlResult',
    components: {
        BAlert,
        BBadge,
        BButton,
        BButtonGroup,
        BPagination,
        BPopover,
        BSpinner,
    },
    props: {
        controlRun: {
            type: Object,
            required: true,
        },
        manifestName: {
            type: String,
            default: '',
        },
        sourceName: {
            type: String,
            default: '',
        },
        versionLabel: {
            type: String,
            default: '',
        },
        starting: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            page: 1,
            perPage: 25,
            rowFilter: 'all',
        }
    },
    computed: {
        succeeded() {
            return this.controlRun.status === 'succeeded'
        },
        canStart() {
            return this.succeeded
                && this.controlRun.persisted !== false
                && Boolean(this.controlRun.id)
        },
        mappingFacts() {
            return this.controlRun.mappingFacts || {}
        },
        mappingFactCards() {
            return FACTS
                .filter(fact => Object.prototype.hasOwnProperty.call(this.mappingFacts, fact.key))
                .map(fact => ({...fact, value: this.mappingFacts[fact.key]}))
        },
        previewStatistics() {
            const statistics = this.controlRun.previewStatistics || {}
            return Object.entries(statistics).map(([key, value]) => ({
                key,
                label: this.humanizeKey(key),
                value,
            }))
        },
        warnings() {
            return Array.isArray(this.controlRun.warnings) ? this.controlRun.warnings : []
        },
        columns() {
            return Array.isArray(this.controlRun.columns) ? this.controlRun.columns : []
        },
        rows() {
            return Array.isArray(this.controlRun.rows) ? this.controlRun.rows : []
        },
        rowFilterOptions() {
            return [
                {value: 'all', label: 'All', count: this.rows.length},
                {value: 'valid', label: 'Valid', count: this.rows.filter(row => row.valid).length},
                {value: 'invalid', label: 'Invalid', count: this.rows.filter(row => !row.valid).length},
            ]
        },
        filteredRows() {
            if (this.rowFilter === 'valid') {
                return this.rows.filter(row => row.valid)
            }
            if (this.rowFilter === 'invalid') {
                return this.rows.filter(row => !row.valid)
            }
            return this.rows
        },
        paginatedRows() {
            const start = (this.page - 1) * this.perPage
            return this.filteredRows.slice(start, start + this.perPage)
        },
        pageStart() {
            return (this.page - 1) * this.perPage + 1
        },
        pageEnd() {
            return Math.min(this.page * this.perPage, this.filteredRows.length)
        },
        globalIssues() {
            const issues = Array.isArray(this.controlRun.issues) ? this.controlRun.issues : []
            return issues.filter(issue => !this.issueHasVisibleRow(issue))
        },
    },
    methods: {
        selectRowFilter(filter) {
            this.rowFilter = filter
            this.page = 1
        },
        cell(row, key) {
            return row && row.values && row.values[key]
                ? row.values[key]
                : {state: 'not_mapped', sourceValue: null, value: null, issues: []}
        },
        cellIssues(cell) {
            return cell && Array.isArray(cell.issues) ? cell.issues : []
        },
        rowIssues(row) {
            return row && Array.isArray(row.issues) ? row.issues : []
        },
        cellClass(cell) {
            return {
                'import-control-result__cell-container--invalid': this.cellIssues(cell).length > 0
                    || cell.state === 'invalid',
                'import-control-result__cell-container--empty': [
                    'not_mapped',
                    'missing',
                    'explicit_empty',
                ].includes(cell.state),
                'import-control-result__cell-container--unresolved': cell.state === 'unresolved',
            }
        },
        displayValue(value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return '—'
            }
            if (typeof value === 'object') {
                return JSON.stringify(value)
            }
            return String(value)
        },
        showSourceValue(cell) {
            return typeof cell.sourceValue !== 'undefined'
                && JSON.stringify(cell.sourceValue) !== JSON.stringify(cell.value)
        },
        sourceRowLabel(row) {
            return row.source && row.source.row ? `Row ${row.source.row}` : 'Unknown row'
        },
        sourceTabLabel(row) {
            if (!row.source) {
                return 'Unknown tab'
            }
            return row.source.physicalTab || row.source.logicalTab || 'Unknown tab'
        },
        columnTargetId(columnIndex) {
            return `import-control-column-${columnIndex}`
        },
        rowTargetId(rowIndex) {
            return `import-control-row-${this.page}-${rowIndex}`
        },
        cellTargetId(rowIndex, columnIndex) {
            return `import-control-cell-${this.page}-${rowIndex}-${columnIndex}`
        },
        issueHasVisibleRow(issue) {
            if (!issue || typeof issue !== 'object' || !issue.row) {
                return false
            }
            return this.rows.some(row => {
                const source = row.source || {}
                return source.row === issue.row
                    && (!issue.physicalTab || source.physicalTab === issue.physicalTab)
            })
        },
        issueLocation(issue) {
            if (!issue || typeof issue !== 'object') {
                return ''
            }
            return [
                issue.physicalTab ? `Tab ${issue.physicalTab}` : null,
                issue.row ? `row ${issue.row}` : null,
                issue.canonicalField || null,
            ].filter(Boolean).join(' · ')
        },
        humanizeKey(key) {
            const label = key
                .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                .replace(/[_-]+/g, ' ')
            return label.charAt(0).toUpperCase() + label.slice(1)
        },
    },
}
</script>

<style lang="scss" scoped>
.import-control-result__context {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    overflow: hidden;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #f8f8f8;
}

.import-control-result__context > div {
    display: flex;
    flex-direction: column;
    gap: .25rem;
    min-width: 0;
    padding: .75rem 1rem;
}

.import-control-result__context > div + div {
    border-left: 1px solid #ebe9f1;
}

.import-control-result__context span {
    color: #45434c;
    font-size: .8rem;
}

.import-control-result__context strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.import-control-result__heading,
.import-control-result__preview-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.import-control-result__facts,
.import-control-result__statistics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
    gap: .75rem;
}

.import-control-result__facts article,
.import-control-result__statistics article {
    padding: .75rem 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #f8f8f8;
}

.import-control-result__facts strong,
.import-control-result__statistics strong {
    display: block;
    margin-top: .25rem;
    color: #45434c;
    font-size: 1.35rem;
}

.import-control-result__global-issues {
    display: grid;
    gap: .5rem;
}

.import-control-result__global-issues article {
    display: flex;
    align-items: flex-start;
    gap: .75rem;
    padding: .75rem 1rem;
    color: #ea5455;
    border: 1px solid rgba(234, 84, 85, .3);
    border-radius: .357rem;
    background: rgba(234, 84, 85, .04);
}

.import-control-result__preview {
    overflow: hidden;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.import-control-result__preview-heading {
    padding: 1rem;
    border-bottom: 1px solid #ebe9f1;
}

.import-control-result__table-wrap {
    overflow-x: auto;
}

.import-control-result__table {
    min-width: 100%;
    width: max-content;
}

.import-control-result__table th,
.import-control-result__table td {
    min-width: 13rem;
    max-width: 20rem;
    vertical-align: top;
    border-color: #ebe9f1;
}

.import-control-result__table th {
    background: #f8f8f8;
    white-space: nowrap;
}

.import-control-result__table .import-control-result__source-column {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: 10rem;
    max-width: 12rem;
    background: #fff;
}

.import-control-result__table th.import-control-result__source-column {
    z-index: 2;
    background: #f8f8f8;
}

.import-control-result__row--invalid .import-control-result__source-column {
    background: #fff8f8;
}

.import-control-result__row-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
}

.import-control-result__row-status--valid {
    color: #28c76f;
    background: rgba(40, 199, 111, .12);
}

.import-control-result__row-status--invalid {
    color: #ea5455;
    background: rgba(234, 84, 85, .12);
}

.import-control-result__row-issues,
.import-control-result__info {
    padding: 0;
    color: #ea5455;
    border: 0;
    background: transparent;
}

.import-control-result__info {
    display: inline-flex;
    color: #82868b;
}

.import-control-result__cell-container--invalid {
    background: rgba(234, 84, 85, .07);
    box-shadow: inset 3px 0 0 #ea5455;
}

.import-control-result__cell-container--empty:not(.import-control-result__cell-container--invalid) {
    color: #82868b;
    background: #fafafa;
}

.import-control-result__cell-container--unresolved:not(.import-control-result__cell-container--invalid) {
    background: rgba(255, 159, 67, .08);
    box-shadow: inset 3px 0 0 #ff9f43;
}

.import-control-result__cell {
    min-height: 2.5rem;
    outline: none;
}

.import-control-result__cell-value {
    overflow-wrap: anywhere;
}

.import-control-result__cell-error {
    flex: 0 0 auto;
    color: #ea5455;
}

.import-control-result__popover-issue + .import-control-result__popover-issue {
    margin-top: .75rem;
    padding-top: .75rem;
    border-top: 1px solid #ebe9f1;
}

.import-control-result__empty {
    padding: 2.5rem 1rem;
    color: #82868b;
    text-align: center;
}

.import-control-result__empty--bordered {
    border: 1px dashed #d8d6de;
    border-radius: .357rem;
}

@media (max-width: 767.98px) {
    .import-control-result__context {
        grid-template-columns: 1fr;
    }

    .import-control-result__context > div + div {
        border-top: 1px solid #ebe9f1;
        border-left: 0;
    }

    .import-control-result__heading,
    .import-control-result__preview-heading {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>

<style lang="scss">
// Bootstrap appends popovers to the document body, outside `.manifest-ui`, so the shared
// manifest muted-text override must be repeated on the popover root.
.import-control-result__popover {
    --manifest-secondary-text-color: #45434c;

    .text-muted {
        color: var(--manifest-secondary-text-color) !important;
    }
}
</style>
